import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import ScheduleUpdateModal from '../Schedule/ScheduleUpdateModal';

const MODE_LABELS = { upd_resp: 'P6 Upd Resp', manual: 'My list', both: 'Both' };

const th = { textAlign: 'left', padding: '8px 10px', fontSize: '0.75rem', color: '#475569', fontWeight: 700, whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', fontSize: '0.8rem', color: '#1e293b', verticalAlign: 'top' };

/**
 * Admin → Notifications → Friday schedule updates.
 * One row per job that has schedule data: is the Friday 7 AM request on, who gets it,
 * and an on/off switch. "Set up" opens the same Update modal the job's Schedule page uses.
 */
const FridayUpdateRequestsPage = () => {
  const { instance, accounts } = useMsal();
  const [jobs, setJobs] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyJob, setBusyJob] = useState(null);
  const [modalJob, setModalJob] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const res = await authenticatedApiCall(instance, accounts, 'GET', '/schedule-baseline/updater-emails/overview');
      if (res.data.success) {
        setJobs(res.data.data.jobs || []);
        setStartDate(res.data.data.startDate || null);
      } else setError(res.data.error || 'Could not load');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load');
    } finally { setLoading(false); }
  }, [instance, accounts]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (job) => {
    setBusyJob(job.jobNumber);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'PATCH', `/schedule-baseline/${job.jobNumber}/updater-emails`, { enabled: !job.enabled });
      if (res.data.success) await load();
      else setError(res.data.error || 'Could not save');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save');
    } finally { setBusyJob(null); }
  };

  const manualEmails = (job) => (Array.isArray(job.manualRecipients) ? job.manualRecipients.length : 0);
  const audience = (job) => {
    const parts = [];
    if (job.mode !== 'manual') parts.push(job.updRespValues ? `${job.updRespValues} Upd Resp code(s) on ${job.withUpdResp} activities` : 'no Upd Resp on the schedule');
    if (job.mode !== 'upd_resp') parts.push(job.manualRows ? `${job.manualRows} row(s) on my list, ${manualEmails(job)} e-mail(s)` : 'my list is empty');
    if (job.extraCc?.length) parts.push(`extra CC ${job.extraCc.join(', ')}`);
    return parts.join(' · ');
  };

  const onCount = jobs.filter(j => j.enabled).length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#0f172a' }}>Friday schedule updates</h3>
          <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#475569', maxWidth: 760, lineHeight: 1.5 }}>
            Every <strong>Friday 7:00 AM ET</strong> the portal e-mails the people responsible for updating each job's schedule,
            on behalf of Project Controls: two PDFs filtered to their part of the schedule (3-week look-ahead and full schedule),
            due back by Sunday midnight, CC the job's PM and Project Controls. One switch per job — flip it here or under the
            job's <strong>Schedule → Update</strong> button. Use <strong>Set up</strong> to choose who receives it.
            {startDate && <> First send: <strong>{startDate}</strong>.</>}
          </p>
        </div>
        <div style={{ fontSize: '0.8rem', color: '#334155', backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 6, padding: '6px 10px', whiteSpace: 'nowrap' }}>
          {onCount} of {jobs.length} job(s) on
        </div>
      </div>

      {error && <div style={{ padding: '8px 12px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: '0.8rem', marginBottom: 10 }}>{error}</div>}
      {loading && <div style={{ padding: 16, color: '#64748b', fontSize: '0.85rem' }}>Loading…</div>}

      {!loading && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'auto', backgroundColor: 'white' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={th}>Friday e-mail</th>
                <th style={th}>Job</th>
                <th style={th}>Audience</th>
                <th style={th}>Who gets it</th>
                <th style={th}>PM in CC</th>
                <th style={{ ...th, textAlign: 'right' }}>Activities</th>
                <th style={th}>Data date</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 && (
                <tr><td colSpan={8} style={{ ...td, color: '#64748b', textAlign: 'center', padding: 20 }}>No job has schedule data yet.</td></tr>
              )}
              {jobs.map(job => {
                const nobody = (job.mode !== 'manual' ? !job.updRespValues : true) && (job.mode !== 'upd_resp' ? manualEmails(job) === 0 : true);
                return (
                  <tr key={job.jobNumber} style={{ borderTop: '1px solid #f1f5f9', backgroundColor: job.enabled ? '#f0fdf4' : 'white' }}>
                    <td style={td}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: 600, color: job.enabled ? '#166534' : '#64748b' }}>
                        <input type="checkbox" checked={!!job.enabled} disabled={busyJob === job.jobNumber} onChange={() => toggle(job)} />
                        {job.enabled ? 'On' : 'Off'}
                      </label>
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{job.jobNumber}</div>
                      <div style={{ color: '#64748b', fontSize: '0.75rem' }}>{job.jobName || <em>not in Projects</em>}</div>
                    </td>
                    <td style={td}>{MODE_LABELS[job.mode] || job.mode}</td>
                    <td style={{ ...td, color: nobody ? '#b45309' : '#1e293b' }}>
                      {audience(job)}
                      {job.enabled && nobody && <div style={{ color: '#b91c1c', fontWeight: 600, fontSize: '0.75rem' }}>On, but nobody would receive it.</div>}
                    </td>
                    <td style={{ ...td, color: job.pmEmails?.length ? '#1e293b' : '#b45309', fontSize: '0.75rem' }}>
                      {job.pmEmails?.length ? job.pmEmails.join(', ') : 'none assigned'}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>{job.activities}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{job.dataDate || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button onClick={() => setModalJob(job.jobNumber)}
                        style={{ padding: '4px 10px', fontSize: '0.75rem', border: '1px solid #c7d2fe', backgroundColor: '#eef2ff', color: '#3730a3', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
                        Set up
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modalJob && (
        <ScheduleUpdateModal isOpen onClose={() => { setModalJob(null); load(); }} jobNumber={modalJob} />
      )}
    </div>
  );
};

export default FridayUpdateRequestsPage;
